'use client'

import { useRef } from 'react'
import { motion, useInView } from 'motion/react'
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { useLanguage } from '@/components/i18n/LanguageProvider'
import fr from '@/i18n/fr.json'
import en from '@/i18n/en.json'
import type { ApiTestimonial } from '@/lib/shop/api'

type ModernTestimonialsProps = {
  testimonials: ApiTestimonial[]
}

export function ModernTestimonials({ testimonials }: ModernTestimonialsProps) {
  const ref = useRef<HTMLDivElement | null>(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })
  const sliderRef = useRef<HTMLDivElement | null>(null)
  const { language } = useLanguage()
  const copy = language === 'fr' ? fr : en
  const showCarousel = testimonials.length > 3

  const scroll = (direction: 'left' | 'right') => {
    if (!sliderRef.current) return
    const amount = sliderRef.current.clientWidth * 0.8
    sliderRef.current.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' })
  }

  const renderCard = (t: ApiTestimonial, index: number) => (
    <motion.div
      key={t.id || `${t.name}-${index}`}
      className="group flex min-w-[280px] flex-1"
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.05 }}
    >
      <div className="flex h-full flex-col rounded-3xl border border-border bg-card p-8 transition-all duration-500 hover:border-primary/20 hover:shadow-xl w-full">
        <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 group-hover:bg-primary/20">
          <Quote className="h-6 w-6 text-primary" />
        </div>
        <div className="mb-4 flex space-x-1">
          {Array.from({ length: Math.max(1, Math.min(5, t.rating || 5)) }).map((_, i) => (
            <Star key={`${t.id}-${i}`} className="h-5 w-5 fill-secondary text-secondary" />
          ))}
        </div>
        <p className="mb-6 flex-1 text-foreground/80">&quot;{t.text}&quot;</p>
        <div className="flex items-center space-x-4 border-t border-border pt-6">
          <Avatar className="h-12 w-12">
            <AvatarFallback className="bg-primary/10 text-primary">{t.avatar || t.name.slice(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium text-foreground">{t.name}</p>
            {t.role && <p className="text-sm text-muted-foreground">{t.role}</p>}
          </div>
        </div>
      </div>
    </motion.div>
  )

  return (
    <section id="testimonials" ref={ref} className="bg-background py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div className="mb-16 text-center" initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}>
          <span className="mb-6 inline-block rounded-full bg-secondary/10 px-4 py-2 text-sm text-secondary">{copy.testimonials.badge}</span>
          <h2 className="mb-6 text-4xl tracking-tight sm:text-5xl lg:text-6xl">
            {copy.testimonials.titleLine1}
            <br />
            <span className="text-primary">{copy.testimonials.titleLine2}</span>
          </h2>
          <p className="mx-auto max-w-2xl text-xl text-muted-foreground">{copy.testimonials.subtitle}</p>
        </motion.div>

        {showCarousel ? (
          <div className="relative">
            <div ref={sliderRef} className="flex gap-6 overflow-hidden">
              {testimonials.map((t, i) => renderCard(t, i))}
            </div>
            <div className="pointer-events-none absolute inset-y-0 left-0 right-0 flex items-center justify-between">
              <button
                className="pointer-events-auto ml-2 flex h-10 w-10 items-center justify-center rounded-full border border-border bg-background/90 shadow"
                onClick={() => scroll('left')}
                aria-label="Précédent"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                className="pointer-events-auto mr-2 flex h-10 w-10 items-center justify-center rounded-full border border-border bg-background/90 shadow"
                onClick={() => scroll('right')}
                aria-label="Suivant"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {testimonials.map((t, i) => renderCard(t, i))}
          </div>
        )}

        <motion.div className="mt-16 grid grid-cols-2 gap-8 md:grid-cols-4" initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.4 }}>
          {[
            { value: '5000+', label: copy.testimonials.stats.clients },
            { value: '4.9/5', label: copy.testimonials.stats.rating },
            { value: '98%', label: copy.testimonials.stats.satisfaction },
            { value: '24h', label: copy.testimonials.stats.delivery },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="mb-2 text-4xl text-primary">{stat.value}</p>
              <p className="text-sm text-muted-foreground">{stat.label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}
